import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import api from '../utils/api';
import { useToast, InlineSpinner } from './UI';

const MAX_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png'];

function validateFile(file, toast) {
  if (!ALLOWED_TYPES.includes(file.type)) {
    toast('Only JPEG or PNG images are allowed', 'error');
    return false;
  }
  if (file.size > MAX_SIZE) {
    toast('Image exceeds the 5MB limit', 'error');
    return false;
  }
  return true;
}

// Circular profile picture with optional upload/remove controls.
//
// Two modes:
//  - Immediate (pass `uploadUrl`/`deleteUrl`): an existing record already has
//    an id, so a picked file uploads right away. The displayed image itself
//    is fetched as an authenticated blob (like FileAttachments' file
//    preview) rather than a plain <img src>, since the picture endpoint
//    sits behind the same JWT auth as everything else and a bare <img> tag
//    can't carry an Authorization header.
//  - Pending (pass `uploadUrlFor(id)` instead): used on a "create" form
//    where there's no record id yet. A picked file is only staged locally
//    (previewed via a local object URL) — the parent form calls
//    ref.flush(newId) after the record is actually saved, mirroring how
//    FileAttachments queues files until then. Nothing is sent to the server
//    if the user cancels out of the form.
const ProfilePicture = forwardRef(function ProfilePicture({
  hasPicture, getUrl, uploadUrl, deleteUrl, uploadUrlFor,
  editable = false, size = 96, onChange,
}, ref) {
  const toast = useToast();
  const pending = !uploadUrl && !!uploadUrlFor;
  const [objectUrl, setObjectUrl] = useState(null);
  const [queuedFile, setQueuedFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (pending) return;
    let cancelled = false;
    let currentUrl = null;
    if (hasPicture && getUrl) {
      api.get(getUrl, { responseType: 'blob' }).then(res => {
        if (cancelled) return;
        currentUrl = URL.createObjectURL(res.data);
        setObjectUrl(currentUrl);
      }).catch(() => { if (!cancelled) setObjectUrl(null); });
    } else {
      setObjectUrl(null);
    }
    return () => { cancelled = true; if (currentUrl) URL.revokeObjectURL(currentUrl); };
  }, [pending, hasPicture, getUrl]);

  useImperativeHandle(ref, () => ({
    hasQueued: () => !!queuedFile,
    flush: async (targetId) => {
      if (!queuedFile || !targetId) return;
      const fd = new FormData(); fd.append('file', queuedFile);
      try {
        await api.post(uploadUrlFor(targetId), fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } catch (err) {
        toast(`Failed to upload profile picture: ${err.response?.data?.message || 'upload failed'}`, 'error');
      }
      setQueuedFile(null);
    },
  }));

  const handlePick = e => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !validateFile(file, toast)) return;
    if (pending) {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setObjectUrl(URL.createObjectURL(file));
      setQueuedFile(file);
      return;
    }
    uploadNow(file);
  };

  const uploadNow = async file => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      await api.post(uploadUrl, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast('Profile picture updated', 'success');
      onChange?.();
    } catch (err) {
      toast(err.response?.data?.message || 'Upload failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (pending) {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setObjectUrl(null);
      setQueuedFile(null);
      return;
    }
    setBusy(true);
    try {
      await api.delete(deleteUrl);
      toast('Profile picture removed', 'success');
      onChange?.();
    } catch (err) {
      toast(err.response?.data?.message || 'Remove failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{
        width: size, height: size, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
        background: 'var(--sms-sur2)', border: '1px solid var(--sms-bdr)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {objectUrl
          ? <img src={objectUrl} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <i className="bi bi-person-fill" style={{ fontSize: size * 0.5, color: 'var(--sms-txt3)' }} />}
      </div>
      {editable && (
        <div className="d-flex flex-column gap-1">
          <label className="sms-btn sms-btn-outline" style={{ fontSize: 12, cursor: 'pointer', marginBottom: 0 }}>
            {busy ? <InlineSpinner /> : <i className="bi bi-camera" />} {objectUrl ? 'Change picture' : 'Upload picture'}
            <input ref={inputRef} type="file" accept={ALLOWED_TYPES.join(',')} style={{ display: 'none' }} onChange={handlePick} disabled={busy} />
          </label>
          {objectUrl && (
            <button type="button" className="sms-btn sms-btn-outline-danger" style={{ fontSize: 12 }} onClick={handleRemove} disabled={busy}>
              <i className="bi bi-trash" /> Remove
            </button>
          )}
          <span style={{ fontSize: 12, color: 'var(--sms-txt3)' }}>Optional — JPEG or PNG, max 5MB</span>
        </div>
      )}
    </div>
  );
});

export default ProfilePicture;
